<template>
  <div
    class="kpi-tile"
    :class="[`kpi-tile--${variant}`, { 'kpi-tile--pulse': pulse }]"
  >
    <div class="kpi-tile__icon">
      <feather-icon
        :icon="icon"
        size="22"
      />
    </div>
    <div class="kpi-tile__body">
      <div class="kpi-tile__label">
        {{ label }}
      </div>
      <div class="kpi-tile__value">
        <b-spinner
          v-if="loading"
          small
        />
        <span v-else>{{ value }}</span>
      </div>
      <div
        v-if="hint"
        class="kpi-tile__hint"
      >
        {{ hint }}
      </div>
      <slot />
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'KpiTile',
  components: { BSpinner },
  props: {
    value: { type: [String, Number], default: '—' },
    label: { type: String, required: true },
    icon: { type: String, default: 'ActivityIcon' },
    // primary | info | success | warning | danger | secondary
    variant: { type: String, default: 'primary' },
    hint: { type: String, default: null },
    pulse: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
}
</script>

<style scoped>
.kpi-tile {
  --kpi-color: #7367f0;
  --kpi-soft: #ede9fd;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-left: 4px solid var(--kpi-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.kpi-tile__icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--kpi-soft);
  color: var(--kpi-color);
}

.kpi-tile__body {
  min-width: 0;
  flex: 1;
}

.kpi-tile__label {
  font-size: 11px;
  color: #6e6b7b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.kpi-tile__value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.15;
  color: var(--kpi-color);
  font-variant-numeric: tabular-nums;
}

.kpi-tile__hint {
  font-size: 12px;
  color: #82868b;
  margin-top: 2px;
}

.kpi-tile--info { --kpi-color: #00cfe8; --kpi-soft: #e0f9fc; }
.kpi-tile--success { --kpi-color: #28c76f; --kpi-soft: #e8faf0; }
.kpi-tile--warning { --kpi-color: #ff9f43; --kpi-soft: #fff3e8; }
.kpi-tile--danger { --kpi-color: #ea5455; --kpi-soft: #fce5e6; }
.kpi-tile--secondary { --kpi-color: #5e5873; --kpi-soft: #f3f2f7; }

.kpi-tile--pulse {
  border-color: var(--kpi-color);
  animation: kpi-tile-pulse 2s ease-in-out infinite;
}

@keyframes kpi-tile-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.3); }
  50% { box-shadow: 0 0 0 6px rgba(234, 84, 85, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .kpi-tile--pulse { animation: none; }
}

.dark-layout .kpi-tile {
  background: #283046;
  border-color: #3b4253;
  border-left-color: var(--kpi-color);
}
.dark-layout .kpi-tile__label,
.dark-layout .kpi-tile__hint {
  color: #b4b7bd;
}
.dark-layout .kpi-tile--secondary { --kpi-color: #d0d2d6; --kpi-soft: #3b4253; }
.dark-layout .kpi-tile__icon { background: rgba(255, 255, 255, 0.06); }
</style>
